import React, { PureComponent } from 'react';
import { toastr } from 'react-redux-toastr';

import {
  releaseLuciaWhatsappChat,
  sendWhatsappHumanMessage,
  takeoverWhatsappChat,
} from '../../../services/operational/whatsappChats';
import ChatMessagesList from './ChatMessagesList';
import {
  ChatActionButton,
  ChatActions,
  ChatBackButton,
  ChatComposer,
  ChatComposerInput,
  ChatHeaderMain,
  ChatMessagesViewport,
  ChatSection,
  ChatSectionHeader,
  ChatSectionSubtitle,
  ChatSectionTitle,
  EmptyChatHint,
  PendingQuestionAlert,
  TakeoverBadge,
} from './chatsWhatsapp.styles';

const actorLabel = (actor) => {
  if (actor === 'assistant') return 'Lúcia';
  if (actor === 'pretendente' || actor === 'user') return 'Cliente';
  if (actor === 'colaborador' || actor === 'cliente') return 'Atendente (painel)';
  if (actor === 'corretor') return 'Corretor';
  return actor || '—';
};

const messageSenderLabel = (msg) => msg.sender_label || actorLabel(msg.actor);

class ChatWhatsappPanel extends PureComponent {
  state = {
    draft: '',
    sending: false,
    actionLoading: false,
  };

  viewportRef = React.createRef();

  componentDidUpdate(prevProps) {
    if (prevProps.messages.length !== this.props.messages.length) {
      this.scrollToBottom();
    }
  }

  scrollToBottom = () => {
    const el = this.viewportRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  };

  onTakeover = async () => {
    const { token, conversation, onConversationUpdated } = this.props;
    if (!token || !conversation) return;
    this.setState({ actionLoading: true });
    try {
      const res = await takeoverWhatsappChat(token, conversation.uuid);
      if (res.status >= 400) {
        toastr.error('Chats', (res.data && res.data.message) || 'Não foi possível assumir a conversa.');
        return;
      }
      onConversationUpdated(res.data.data);
      this.props.onRefreshMessages?.();
      toastr.success('Chats', 'Você assumiu a conversa. A Lúcia está pausada.');
    } catch (e) {
      toastr.error('Chats', 'Erro ao assumir conversa.');
    } finally {
      this.setState({ actionLoading: false });
    }
  };

  onReleaseLucia = async () => {
    const { token, conversation, onConversationUpdated } = this.props;
    if (!token || !conversation) return;
    this.setState({ actionLoading: true });
    try {
      const res = await releaseLuciaWhatsappChat(token, conversation.uuid);
      if (res.status >= 400) {
        toastr.error('Chats', (res.data && res.data.message) || 'Não foi possível reativar a Lúcia.');
        return;
      }
      onConversationUpdated(res.data.data);
      this.props.onRefreshMessages?.();
      toastr.success('Chats', 'Lúcia reativada para esta conversa.');
    } catch (e) {
      toastr.error('Chats', 'Erro ao reativar a Lúcia.');
    } finally {
      this.setState({ actionLoading: false });
    }
  };

  onSubmit = async (e) => {
    e.preventDefault();
    const { token, conversation, onMessageSent } = this.props;
    const text = this.state.draft.trim();
    if (!token || !conversation || !text || this.state.sending) return;

    this.setState({ sending: true });
    try {
      const res = await sendWhatsappHumanMessage(token, conversation.uuid, text);
      if (res.status >= 400) {
        toastr.error('Chats', (res.data && res.data.message) || 'Falha ao enviar mensagem.');
        return;
      }
      this.setState({ draft: '' });
      onMessageSent(res.data);
    } catch (err) {
      toastr.error('Chats', 'Erro ao enviar mensagem.');
    } finally {
      this.setState({ sending: false });
    }
  };

  render() {
    const { conversation, messages, loadingMessages, onBack, chatTipo = 'coleta' } = this.props;
    const { draft, sending, actionLoading } = this.state;

    if (!conversation) {
      return null;
    }

    const takeoverActive = conversation.human_takeover_active;
    const statusKey = String(conversation.conversation_status || conversation.status || '').toLowerCase();
    const isFinalizado = statusKey === 'finalizado';
    const canTakeover =
      chatTipo !== 'solicita' && conversation.can_takeover === true && !isFinalizado;
    const pendingQuestion = conversation.pending_corretor_question;

    const pretendenteNome = conversation.pretendente_nome;
    const pretendenteTelefone = conversation.pretendente_telefone;
    const usuarioImobiliaria = conversation.usuario_imobiliaria;
    const solicitacaoId = conversation.solicitacao_id;
    const titleName =
      pretendenteNome || usuarioImobiliaria || conversation.contact_name || 'Conversa';
    const titlePhone = pretendenteTelefone || conversation.whatsapp_from || null;
    const titleSuffix = (() => {
      if (pretendenteNome && titlePhone && titleName !== titlePhone) {
        return ` · ${titlePhone}`;
      }
      if (solicitacaoId) {
        return ` · #${solicitacaoId}`;
      }
      if (titlePhone && titleName !== titlePhone) {
        return ` · ${titlePhone}`;
      }
      return '';
    })();

    const statusBadgeLabel = (() => {
      if (!canTakeover) return 'Somente visualização';
      if (takeoverActive) return 'Atendimento humano ativo';
      return 'Lúcia ativa';
    })();

    return (
      <ChatSection>
        <ChatSectionHeader>
          <ChatHeaderMain>
            {typeof onBack === 'function' && (
              <ChatBackButton type="button" onClick={onBack} aria-label="Voltar para a lista de conversas">
                ← Voltar
              </ChatBackButton>
            )}
            <div style={{ minWidth: 0 }}>
              <ChatSectionTitle>
                {titleName}
                {titleSuffix}
              </ChatSectionTitle>
              {usuarioImobiliaria && pretendenteNome ? (
                <ChatSectionSubtitle>Usuário/Imobiliária: {usuarioImobiliaria}</ChatSectionSubtitle>
              ) : null}
              <TakeoverBadge $active={canTakeover && takeoverActive} style={{ marginTop: 8 }}>
                {statusBadgeLabel}
              </TakeoverBadge>
              {canTakeover && pendingQuestion && pendingQuestion.question && !takeoverActive && (
                <PendingQuestionAlert style={{ marginTop: 10, marginLeft: 0, marginRight: 0, marginBottom: 0 }}>
                  <strong>Dúvida aguardando resposta</strong>
                  {pendingQuestion.question}
                </PendingQuestionAlert>
              )}
            </div>
          </ChatHeaderMain>
          <ChatActions>
            {canTakeover && !takeoverActive && (
              <ChatActionButton type="button" $primary onClick={this.onTakeover} disabled={actionLoading}>
                {pendingQuestion && pendingQuestion.question ? 'Responder dúvida' : 'Assumir conversa'}
              </ChatActionButton>
            )}
            {canTakeover && takeoverActive && (
              <ChatActionButton type="button" $danger onClick={this.onReleaseLucia} disabled={actionLoading}>
                Reativar Lúcia
              </ChatActionButton>
            )}
          </ChatActions>
        </ChatSectionHeader>

        <ChatMessagesViewport ref={this.viewportRef}>
          {loadingMessages && messages.length === 0 && (
            <EmptyChatHint>Carregando mensagens…</EmptyChatHint>
          )}
          {!loadingMessages && messages.length === 0 && (
            <EmptyChatHint>Nenhuma mensagem nesta conversa ainda.</EmptyChatHint>
          )}
          <ChatMessagesList
            messages={messages}
            token={this.props.token}
            conversationUuid={conversation.uuid}
            messageSenderLabel={messageSenderLabel}
          />
        </ChatMessagesViewport>

        {canTakeover && (
          <ChatComposer onSubmit={this.onSubmit}>
            <ChatComposerInput
              value={draft}
              onChange={(e) => this.setState({ draft: e.target.value })}
              placeholder={
                takeoverActive
                  ? 'Mensagem enviada pelo número da Lúcia no WhatsApp, em seu nome…'
                  : 'Assuma a conversa para enviar mensagens pelo painel'
              }
              disabled={!takeoverActive || sending}
            />
            <ChatActionButton type="submit" $primary disabled={!takeoverActive || sending || !draft.trim()}>
              Enviar
            </ChatActionButton>
          </ChatComposer>
        )}
      </ChatSection>
    );
  }
}

export default ChatWhatsappPanel;
